import React from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import {
  Badge,
  Button,
  Input,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row
} from "reactstrap"

import ButtonLoader from "../../../components/Common/ButtonLoader"
import { useAttendanceModal } from "../hooks/useAttendanceModal"

const AttendanceModal = ({ isOpen, onClose, schedule, onAttendanceSaved }) => {
  const {
    members,
    loadingMembers,
    searchText,
    searchResults,
    justAddedId,
    attendanceStats,
    isDirty,
    showConfirm,
    title,
    setSearchText,
    setShowConfirm,
    isLoading,
    handleSelectSearchMember,
    handleMarkAbsence,
    handleMarkPresent,
    handleChangeJustification,
    handleSave,
    handleClose,
    handleForceClose,
    handleGoToProfile
  } = useAttendanceModal({ isOpen, onClose, schedule, onAttendanceSaved });


  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        toggle={handleClose}
        size="xl"
        centered
        backdrop="static"
        modalClassName="premium-modal"
        contentClassName="premium-modal__content"
      >
        <ModalHeader toggle={handleClose} className="premium-modal__header">
          <div className="d-flex align-items-center gap-2">
            <i className="mdi mdi-account-check-outline text-primary fs-4"></i>
            <div>
              <h5 className="mb-0 fw-bold">Controle de Presença</h5>
              <small className="text-muted fw-medium">{title}</small>
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="premium-modal__body p-4" style={{ maxHeight: "78vh", overflowY: "auto" }}>

          <style>{`
            .modern-attendance-item { display: flex; align-items: center; background: #fff; border: 1px solid rgba(0,0,0,0.06); border-radius: 4px; padding: 16px; margin-bottom: 12px; box-shadow: 0 4px 15px rgba(0,0,0,0.02); transition: all 0.3s ease; }
            .modern-attendance-item:hover { transform: translateY(-2px); box-shadow: 0 8px 25px rgba(0,0,0,0.06); border-color: rgba(var(--bs-primary-rgb), 0.2); }
            .modern-attendance-item.is-absent-modern { background: #fdfdfd; opacity: 0.85; border: 1px dashed rgba(0,0,0,0.15); }
            .modern-attendance-item.is-new-modern { border-color: var(--bs-primary); box-shadow: 0 0 0 2px rgba(var(--bs-primary-rgb), 0.2); }
            .modern-avatar-wrap { position: relative; margin-right: 16px; flex-shrink: 0; }
            .modern-avatar { width: 52px; height: 52px; object-fit: cover; border-radius: 50%; border: 1px solid rgba(0,0,0,0.08); box-shadow: 0 2px 8px rgba(0,0,0,0.05); }
            .modern-avatar-placeholder { width: 52px; height: 52px; border-radius: 50%; background: var(--bs-light); color: var(--bs-gray-500); display: flex; justify-content: center; align-items: center; font-size: 24px; border: 1px solid rgba(0,0,0,0.05); box-shadow: 0 2px 8px rgba(0,0,0,0.02); }
            .modern-ex-badge { position: absolute; bottom: -2px; right: -4px; background: var(--bs-info); color: #fff; font-size: 10px; font-weight: bold; padding: 2px 6px; border-radius: 12px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); border: 2px solid #fff; z-index: 2; }
            .modern-content { flex-grow: 1; min-width: 0; }
            .modern-actions { display: flex; gap: 8px; align-items: center; }
            .modern-summary-card { background: linear-gradient(135deg, rgba(var(--bs-primary-rgb), 0.04) 0%, rgba(var(--bs-primary-rgb), 0.08) 100%); border-radius: 4px; padding: 24px; border: 1px solid rgba(var(--bs-primary-rgb), 0.08); }
            .modern-stat-box { background: #fff; border-radius: 4px; padding: 12px 20px; display: flex; flex-direction: column; align-items: center; justify-content: center; box-shadow: 0 2px 10px rgba(0,0,0,0.03); min-width: 100px; border: 1px solid rgba(0,0,0,0.03); }
            .modern-info-row { display: flex; align-items: center; gap: 12px; }
            .modern-icon-box { width: 40px; height: 40px; border-radius: 4px; background: #fff; color: var(--bs-primary); display: flex; align-items: center; justify-content: center; font-size: 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.04); border: 1px solid rgba(0,0,0,0.04); }
          `}</style>

          {/* Class Info Card - Modern */}
          <div className="modern-summary-card mb-4">
            <Row className="align-items-center g-4">
              <Col lg={7}>
                <div className="d-flex flex-column gap-3">
                  <div className="modern-info-row">
                    <div className="modern-icon-box"><i className="mdi mdi-account-star"></i></div>
                    <div>
                      <div className="text-muted small fw-bold text-uppercase tracking-wide">Professor</div>
                      <div className="fw-bold fs-5 text-dark">{schedule?.employeeName || "Não definido"}</div>
                    </div>
                  </div>
                  <div className="d-flex gap-4 flex-wrap">
                    <div className="modern-info-row">
                      <div className="modern-icon-box"><i className="mdi mdi-clock-outline"></i></div>
                      <div>
                        <div className="text-muted small fw-bold text-uppercase tracking-wide">Horário</div>
                        <div className="fw-bold text-dark">{schedule?.startTime} - {schedule?.endTime}</div>
                      </div>
                    </div>
                    <div className="modern-info-row">
                      <div className="modern-icon-box"><i className="mdi mdi-map-marker-radius-outline"></i></div>
                      <div>
                        <div className="text-muted small fw-bold text-uppercase tracking-wide">Local</div>
                        <div className="fw-bold text-dark">{schedule?.areaName || "Geral"}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
              <Col lg={5}>
                <div className="d-flex gap-3 justify-content-lg-end justify-content-center">
                  <div className="modern-stat-box">
                    <span className="modern-stat-value text-success">{attendanceStats.present}</span>
                    <span className="modern-stat-label">Presentes</span>
                  </div>
                  <div className="modern-stat-box">
                    <span className="modern-stat-value text-danger">{attendanceStats.absent}</span>
                    <span className="modern-stat-label">Ausentes</span>
                  </div>
                  <div className="modern-stat-box bg-dark text-white border-0">
                    <span className="modern-stat-value text-white">{attendanceStats.total}</span>
                    <span className="modern-stat-label text-white-50">Total</span>
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          {/* Global Search */}
          <div className="mb-4">
            <div className="position-relative">
              <span className="position-absolute top-50 start-0 translate-middle-y ms-3 z-index-10">
                {isLoading('addExtra') ? <ButtonLoader size="sm" /> : <i className="mdi mdi-magnify text-muted fs-4" />}
              </span>
              <Input
                className="form-control-lg border ps-5 shadow-sm"
                placeholder="Adicionar aluno à chamada (Nome, CPF ou ID)..."
                style={{ fontSize: '1.05rem', borderRadius: '4px', height: '56px', backgroundColor: '#f4f5f7' }}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                disabled={isLoading('addExtra')}
              />
              {searchResults.length > 0 && (
                <div className="search-dropdown shadow-lg border rounded-1 mt-2">
                  {searchResults.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      className="search-dropdown__item"
                      onClick={() => handleSelectSearchMember(c)}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div className="avatar-xs rounded-1 bg-primary-subtle text-primary d-flex align-items-center justify-content-center fw-bold">
                          {c.name?.charAt(0)}
                        </div>
                        <div>
                          <div className="fw-bold text-dark">{c.name}</div>
                          <div className="text-muted small">{c.memberNumber}</div>
                        </div>
                      </div>
                      <i className="mdi mdi-plus-circle-outline fs-4 text-primary opacity-50"></i>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <Row className="g-4">
            {/* List Section: Present */}
            <Col md={12}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="section-title mb-0">
                  <i className="mdi mdi-check-circle-outline text-success me-2"></i>
                  Lista de Presença
                </h6>
                <span className="badge bg-success-subtle text-success rounded-1 px-3">
                  {attendanceStats.present} Alunos
                </span>
              </div>

              <div className="attendance-list-container">
                {loadingMembers ? (
                  <div className="text-center py-4 bg-light rounded-3 border border-dashed">
                    <ButtonLoader color="primary" loading={true} />
                    <p className="text-muted mb-0 mt-2">Carregando alunos matriculados...</p>
                  </div>
                ) : members.filter(c => c.status !== "absent").length === 0 ? (
                  <div className="text-center py-4 bg-light rounded-3 border border-dashed">
                    <p className="text-muted mb-0">Nenhum aluno marcado como presente.</p>
                  </div>
                ) : null}
                <div className="d-flex flex-column gap-2">
                  {members
                    .filter(c => c.status !== "absent")
                    .map(member => {
                      return (
                        <div
                          key={member.id}
                          className={classNames("modern-attendance-item", { "is-new-modern": String(member.id) === String(justAddedId) })}
                        >
                          <div className="modern-avatar-wrap">
                            {member.photo ? (
                              <img src={member.photo} alt={member.name} className="modern-avatar" />
                            ) : (
                              <div className="modern-avatar-placeholder"><i className="mdi mdi-account"></i></div>
                            )}
                            {['experimental', 'single-session', 'trial'].includes(String(member.enrollmentType || member.type).toLowerCase()) && (
                              <span className="modern-ex-badge" title="Experimental">EX</span>
                            )}
                          </div>

                          <div className="modern-content">
                            <div className="fw-bold text-dark fs-5">{member.name}</div>
                            <div className="d-flex gap-2 align-items-center mt-1">
                              {member.memberNumber && <span className="text-muted small fw-bold">#{member.memberNumber}</span>}

                              {member.memberStatus && (
                                <Badge color={member.memberStatus === 'active' ? 'success' : member.memberStatus === 'suspended' ? 'warning' : 'secondary'} className="px-2 pb-1 rounded-1">
                                  {member.memberStatus === 'active' ? 'Ativo' : member.memberStatus === 'suspended' ? 'Suspenso' : member.memberStatus === 'inactive' ? 'Inativo' : String(member.memberStatus)}
                                </Badge>
                              )}

                              {['experimental', 'single-session', 'trial'].includes(String(member.enrollmentType || member.type).toLowerCase()) && (
                                <Badge color="info" className="px-2 pb-1 rounded-1">Experimental</Badge>
                              )}
                            </div>
                          </div>

                          <div className="modern-actions" onClick={(e) => e.stopPropagation()}>
                            <Button
                              color="light"
                              className="btn-icon btn-sm rounded-1 shadow-sm"
                              style={{ width: '32px', height: '32px', padding: 0 }}
                              onClick={() => handleGoToProfile(member.id)}
                              title="Ver Perfil"
                            >
                              <i className="mdi mdi-eye-outline text-primary fs-5"></i>
                            </Button>

                            <Button color="danger" outline className="btn-sm rounded-1 px-3 fw-bold border-1" onClick={() => handleMarkAbsence(member.id)}>
                              <i className="mdi mdi-account-minus-outline me-1 fs-5 align-middle"></i> Faltou
                            </Button>
                          </div>
                        </div>
                      )
                    })}
                </div>
              </div>
            </Col>

            {/* List Section: Absent */}
            <Col md={12}>
              <div className="d-flex align-items-center justify-content-between mt-2 mb-3">
                <h6 className="section-title mb-0">
                  <i className="mdi mdi-close-circle-outline text-danger me-2"></i>
                  Ausentes
                </h6>
                <span className="badge bg-danger-subtle text-danger rounded-1 px-3">
                  {attendanceStats.absent} Alunos
                </span>
              </div>

              <div className="attendance-list-container">
                {attendanceStats.absent === 0 && (
                  <div className="text-center py-4 bg-light rounded-1 border border-dashed opacity-50">
                    <p className="text-muted mb-0">Nenhum aluno marcado como ausente.</p>
                  </div>
                )}
                <div className="d-flex flex-column gap-2">
                  {members
                    .filter(c => c.status === "absent")
                    .map(member => (
                      <div
                        key={member.id}
                        className="modern-attendance-item is-absent-modern"
                      >
                        <div className="modern-avatar-wrap" style={{ opacity: 0.6 }}>
                          {member.photo ? (
                            <img src={member.photo} alt={member.name} className="modern-avatar grayscale" style={{ filter: 'grayscale(100%)' }} />
                          ) : (
                            <div className="modern-avatar-placeholder"><i className="mdi mdi-account"></i></div>
                          )}
                          {['experimental', 'single-session', 'trial'].includes(String(member.enrollmentType || member.type).toLowerCase()) && (
                            <span className="modern-ex-badge" style={{ filter: 'grayscale(100%)' }} title="Experimental">EX</span>
                          )}
                        </div>

                        <div className="modern-content">
                          <div className="fw-bold text-dark fs-5 text-decoration-line-through text-muted">{member.name}</div>
                          <div className="d-flex gap-2 align-items-center mt-1">
                            {member.memberNumber && <span className="text-muted small fw-bold">#{member.memberNumber}</span>}

                            {member.memberStatus && (
                              <Badge color={member.memberStatus === 'active' ? 'success' : member.memberStatus === 'suspended' ? 'warning' : 'secondary'} className="px-2 pb-1 rounded-1">
                                {member.memberStatus === 'active' ? 'Ativo' : member.memberStatus === 'suspended' ? 'Suspenso' : member.memberStatus === 'inactive' ? 'Inativo' : String(member.memberStatus)}
                              </Badge>
                            )}

                            {['experimental', 'single-session', 'trial'].includes(String(member.enrollmentType || member.type).toLowerCase()) && (
                              <Badge color="info" className="px-2 pb-1 rounded-1">Experimental</Badge>
                            )}
                          </div>

                          <div className="mt-2">
                            <Input
                              type="text"
                              placeholder="Adicionar motivo da falta (opcional)..."
                              className="border-dashed bg-white shadow-sm"
                              value={member.justification || ''}
                              onChange={e => handleChangeJustification(member.id, e.target.value)}
                              style={{ maxWidth: '350px', fontSize: '0.9rem' }}
                            />
                          </div>
                        </div>

                        <div className="modern-actions text-end" onClick={(e) => e.stopPropagation()}>
                          <Button
                            color="light"
                            className="btn-icon btn-sm rounded-1 shadow-sm"
                            style={{ width: '32px', height: '32px', padding: 0 }}
                            onClick={() => handleGoToProfile(member.id)}
                            title="Ver Perfil"
                          >
                            <i className="mdi mdi-eye-outline text-primary fs-5"></i>
                          </Button>

                          <Button color="success" className="btn-sm rounded-1 px-3 fw-bold shadow-sm" onClick={() => handleMarkPresent(member.id)}>
                            <i className="mdi mdi-account-plus me-1 fs-5 align-middle"></i> Presente
                          </Button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </Col>
          </Row>
        </ModalBody>

        <div className="premium-modal__footer p-4 d-flex justify-content-between align-items-center bg-light border-top">
          <Button color="secondary" outline className="px-4">
            <i className="mdi mdi-whatsapp me-1"></i> Notificar Alunos
          </Button>
          <div className="d-flex gap-2">
            <Button color="light" className="px-4" onClick={handleClose}>
              Fechar
            </Button>
            <ButtonLoader
              color="primary"
              className="px-4 shadow-primary"
              onClick={handleSave}
              loading={isLoading('save')}
              loadingText="Salvando..."
              disabled={!isDirty && attendanceStats.total > 0}
            >
              Salvar
            </ButtonLoader>
          </div>
        </div>
      </Modal>

      {/* Confirmation Modal for Unsaved Changes */}
      <Modal
        isOpen={showConfirm}
        toggle={() => setShowConfirm(false)}
        centered
        size="md"
        contentClassName="border-0 shadow-lg"
      >
        <div className="p-4 text-center">
          <div className="mb-3">
            <div className="avatar-lg mx-auto">
              <div className="avatar-title bg-warning-subtle text-warning display-4 rounded-circle">
                <i className="mdi mdi-alert-outline"></i>
              </div>
            </div>
          </div>
          <h4 className="mb-2 fw-bold text-dark">Atenção!</h4>
          <p className="text-muted mb-4">
            Você realizou alterações na chamada que ainda não foram salvas.<br />
            Ao sair agora, <strong>todas as alterações serão perdidas.</strong>
          </p>

          <div className="d-flex gap-2 justify-content-center">
            <Button
              color="light"
              className="px-4 w-50"
              onClick={() => setShowConfirm(false)}
            >
              Voltar
            </Button>
            <Button
              color="danger"
              className="px-4 w-50 shadow-danger"
              onClick={handleForceClose}
            >
              Sair sem Salvar
            </Button>
          </div>
        </div>
      </Modal>

    </React.Fragment>
  )
}

AttendanceModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  schedule: PropTypes.shape({
    activityName: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    maxCapacity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    employeeName: PropTypes.string,
    areaName: PropTypes.string,
  }),
}

export default AttendanceModal
